import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  ExternalLink,
  CheckCircle2,
  Compass,
  Wrench,
  Clock,
  ShieldCheck,
  Truck,
  Building2,
  Layers,
  HelpCircle,
  Sparkles,
  FileText,
  HardHat
} from 'lucide-react';
import { COMPANY_INFO, STAFF } from '../data';
import ContactForm from '../components/ContactForm';

export default function Minilaesser() {
  const getOptimizedUrl = (url, width = 1200) => {
    if (url && url.startsWith('https://assets.auxodev.dk/')) {
      return url.replace(
        'https://assets.auxodev.dk/',
        `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
      );
    }
    return url;
  };

  const heroImage = "https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/minilaesser-laessemaskine-udlejning.jpg";
  const actionImage = "https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/minilaesser-arbejde.jpg";
  const liftTeaserImage = "https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/jlg-bomlift-udlejning.png";
  const craneTeaserImage = "https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/minikran-loeft-materiel-udlejning.jpg";
  const catalogUrl = "https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=26";

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>Minilæsser | Vesthimmerlands Materieludlejning</title>
        <meta
          name="description"
          content="Lej en fleksibel og stærk minilæsser til jord-, have- og anlægsarbejde. Hurtig levering hos Vesthimmerlands Materieludlejning i Svenstrup."
        />
        <meta property="og:title" content="Minilæsser | Vesthimmerlands Materieludlejning" />
        <meta
          property="og:description"
          content="Lej en fleksibel og stærk minilæsser til jord-, have- og anlægsarbejde. Hurtig levering hos Vesthimmerlands Materieludlejning i Svenstrup."
        />
        <meta property="og:image" content={heroImage} />
        <link rel="canonical" href="https://vhmu.dk/minilaesser" />
      </Helmet>

      {/* Hero Header Sektion */}
      <section className="relative overflow-hidden bg-brand-light border-b border-brand-grey pt-12 pb-16 md:pt-16 md:pb-24">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          {/* Brødkrumme */}
          <nav className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-400 mb-6">
            <Link to="/" className="hover:text-brand-yellow transition-colors">
              Forside
            </Link>
            <span>/</span>
            <span className="text-gray-500">Produktkatalog</span>
            <span>/</span>
            <span className="text-brand-yellow">Minilæsser</span>
          </nav>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            {/* Tekstkolonne */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/20 px-3.5 py-1.5 text-xs font-black uppercase tracking-wider text-brand-yellow mb-6">
                <Sparkles className="h-3.5 w-3.5" />
                <span>Kompakt & Manøvredygtigt Materiel</span>
              </div>

              <h1 className="text-3xl font-black uppercase tracking-tight text-gray-900 sm:text-4xl md:text-5xl lg:text-5xl leading-[1.1]">
                Lej en minilæsser, <br className="hidden sm:inline" />
                <span className="text-brand-yellow">der passer til opgaven</span>
              </h1>

              <p className="mt-6 text-base sm:text-lg text-gray-700 leading-relaxed font-normal">
                Skal du flytte jord, grus eller andre materialer, kan en minilæsser gøre arbejdet lettere, især når en større læssemaskine fylder for meget. Hos <Link to="/" className="text-gray-900 font-semibold underline decoration-brand-yellow decoration-2 underline-offset-4 hover:text-brand-yellow transition-colors">Vesthimmerlands Materieludlejning</Link> hjælper vi dig med at finde materiel til byggepladsen, anlægsarbejdet eller opgaver i landbruget.
              </p>

              {/* Handlingsknapper */}
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#kontakt-tilbud"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-yellow px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-white shadow-sm hover:bg-brand-yellow-hover transition-all duration-200 cursor-pointer"
                >
                  <span>Få et uforpligtende tilbud</span>
                  <ArrowRight className="h-4 w-4" />
                </a>

                <a
                  href={catalogUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-white border border-brand-grey px-5 py-3.5 text-sm font-bold uppercase tracking-wider text-gray-800 shadow-sm hover:border-brand-yellow hover:text-brand-yellow transition-all duration-200"
                >
                  <span>Se maskiner i online-katalog</span>
                  <ExternalLink className="h-4 w-4" />
                </a>

                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="inline-flex items-center gap-2 text-sm font-bold text-gray-700 hover:text-brand-yellow px-2 py-2 transition-colors"
                >
                  <Phone className="h-4 w-4 text-brand-yellow" />
                  <span>{COMPANY_INFO.phone}</span>
                </a>
              </div>

              {/* USP Highlights */}
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4 pt-8 border-t border-brand-grey/60">
                <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
                  <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span>Kompakt vendeplads</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
                  <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span>Skovl & pallegafler</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
                  <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span>Serviceret & klargjort</span>
                </div>
              </div>
            </div>

            {/* Billedekolonne */}
            <div className="lg:col-span-5">
              <div className="relative mx-auto max-w-lg lg:max-w-none">
                <div className="overflow-hidden rounded-2xl border border-brand-grey bg-white shadow-xl aspect-[4/3]">
                  <img
                    src={getOptimizedUrl(heroImage, 1000)}
                    alt="Kompakt minilæsser til jord- og materialetransport hos Vesthimmerlands Materieludlejning"
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>

                {/* Svævende info-badge */}
                <div className="absolute -bottom-6 -left-4 sm:bottom-4 sm:left-4 rounded-xl bg-white/95 backdrop-blur-sm border border-brand-grey p-4 shadow-lg flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-yellow text-white">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="block text-xs font-bold uppercase tracking-wider text-gray-900">
                      Egne mekanikere
                    </span>
                    <span className="block text-[11px] text-gray-500">
                      Løbende vedligeholdt & driftssikkert
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 1: Hvilke opgaver kan den kompakte maskine hjælpe med? */}
      <section className="py-20 md:py-28 border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="max-w-3xl">
            <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
              Alsidig & Effektiv
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900">
              Hvilke opgaver kan den kompakte maskine hjælpe med?
            </h2>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-7 space-y-6 text-base text-gray-700 leading-relaxed">
              <p>
                En minilæsser er velegnet, når materialer skal flyttes rundt på en byggeplads eller læsses op, hvor der er begrænset plads. Den kompakte størrelse er en fordel mange steder, men maskinen skal stadig passe til materialets vægt, underlaget og den plads, du har til at arbejde.
              </p>
              <p>
                Skal du grave ud, er en minigraver ofte mere oplagt. Skal jorden derimod flyttes efter udgravningen, kan en minilæsser være et godt supplement. Vi udlejer også <Link to="/maskinudlejning" className="text-brand-yellow font-semibold hover:underline">gravemaskiner og dumpere</Link> og hjælper dig gerne med at vurdere, hvilke maskiner der giver mening sammen.
              </p>

              {/* 3 Anvendelsesområder */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
                <div className="rounded-xl border border-brand-grey bg-brand-dark p-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-yellow/10 text-brand-yellow mb-3">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-1">
                    Byggepladsen
                  </h3>
                  <p className="text-xs text-gray-600">
                    Flytning af byggematerialer, paller og oprydning hvor pladsen er trang.
                  </p>
                </div>

                <div className="rounded-xl border border-brand-grey bg-brand-dark p-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-yellow/10 text-brand-yellow mb-3">
                    <Layers className="h-5 w-5" />
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-1">
                    Anlægsarbejde
                  </h3>
                  <p className="text-xs text-gray-600">
                    Hurtig transport af muldjord, grus, sand og belægningssten i haver og indkørsler.
                  </p>
                </div>

                <div className="rounded-xl border border-brand-grey bg-brand-dark p-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-yellow/10 text-brand-yellow mb-3">
                    <Truck className="h-5 w-5" />
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-1">
                    Landbrug & Grønt
                  </h3>
                  <p className="text-xs text-gray-600">
                    Fodring, udmugning og vedligehold i stalde og trange lader.
                  </p>
                </div>
              </div>
            </div>

            {/* Maskinsynergi Kort */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border-2 border-brand-yellow/20 bg-brand-light p-6 sm:p-8">
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-brand-yellow mb-2">
                  Maskinsynergi på byggepladsen
                </h3>
                <h4 className="text-xl font-black uppercase tracking-tight text-gray-900 mb-4">
                  Kombinér for optimal effektivitet
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed mb-6">
                  Når du lejer maskiner hos os, kan vi sammensætte den ideelle maskinpakke, så dit projekt glider uden unødig spildtid:
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3 rounded-lg bg-white p-3.5 border border-brand-grey">
                    <div className="rounded-md bg-brand-yellow/10 p-2 text-brand-yellow shrink-0">
                      <Wrench className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="block text-xs font-bold uppercase text-gray-900">
                        Minigraver + Minilæsser
                      </span>
                      <span className="text-xs text-gray-600">
                        Minigraveren udgraver – minilæsseren transporterer og læsser jorden op.
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 rounded-lg bg-white p-3.5 border border-brand-grey">
                    <div className="rounded-md bg-brand-yellow/10 p-2 text-brand-yellow shrink-0">
                      <Truck className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="block text-xs font-bold uppercase text-gray-900">
                        Dumper + Minilæsser
                      </span>
                      <span className="text-xs text-gray-600">
                        Hurtig påfyldning af dumper ved længere transportveje på grunden.
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-brand-grey flex items-center justify-between">
                  <Link
                    to="/maskinudlejning"
                    className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-yellow hover:text-brand-yellow-hover"
                  >
                    <span>Se maskinudlejning</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>

                  <a
                    href="https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=20"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-900"
                  >
                    <span>Gravemaskiner</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 2: Hvad skal du afklare, før du vælger? (Kreativ Tjekliste & Redskaber) */}
      <section className="bg-brand-dark py-20 md:py-28 border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
              Praktisk Tjekliste
            </span>
            <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900">
              Hvad skal du afklare, før du vælger?
            </h2>
            <p className="mt-4 text-base text-gray-700 leading-relaxed">
              Det vigtigste er ikke kun, om maskinen kan komme gennem en port. Den skal også kunne vende og arbejde dér, hvor opgaven skal løses. Når du kontakter os, er det derfor en hjælp, hvis du allerede har tænkt over adgangsveje, materialer og forholdene på selve arbejdsstedet.
            </p>
          </div>

          {/* 4 Afklaringspunkter i et moderne tjeklistegrid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Punkt 1 */}
            <div className="relative rounded-2xl border border-brand-grey bg-white p-6 shadow-sm hover:border-brand-yellow/40 transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow/10 text-brand-yellow font-black text-lg">
                  01
                </div>
                <Compass className="h-5 w-5 text-gray-400" />
              </div>
              <h3 className="text-base font-bold uppercase tracking-wider text-gray-900 mb-2">
                Adgangsveje
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Mål porte og passager, og se på pladsen til at vende.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-100 text-[11px] text-gray-500">
                Tip: Notér både bredde og frihøjde på gennemkørsler.
              </div>
            </div>

            {/* Punkt 2 */}
            <div className="relative rounded-2xl border border-brand-grey bg-white p-6 shadow-sm hover:border-brand-yellow/40 transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow/10 text-brand-yellow font-black text-lg">
                  02
                </div>
                <Layers className="h-5 w-5 text-gray-400" />
              </div>
              <h3 className="text-base font-bold uppercase tracking-wider text-gray-900 mb-2">
                Materialer
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Fortæl, hvad der skal flyttes, og hvor meget du forventer at håndtere.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-100 text-[11px] text-gray-500">
                Tip: Massefylde varierer meget ml. våd jord og paller.
              </div>
            </div>

            {/* Punkt 3 */}
            <div className="relative rounded-2xl border border-brand-grey bg-white p-6 shadow-sm hover:border-brand-yellow/40 transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow/10 text-brand-yellow font-black text-lg">
                  03
                </div>
                <Truck className="h-5 w-5 text-gray-400" />
              </div>
              <h3 className="text-base font-bold uppercase tracking-wider text-gray-900 mb-2">
                Underlag
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Beskriv, om arbejdet foregår på fast belægning eller blød jord.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-100 text-[11px] text-gray-500">
                Tip: Vi udlejer også køreplader til beskyttelse af græs.
              </div>
            </div>

            {/* Punkt 4 */}
            <div className="relative rounded-2xl border border-brand-grey bg-white p-6 shadow-sm hover:border-brand-yellow/40 transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow/10 text-brand-yellow font-black text-lg">
                  04
                </div>
                <Wrench className="h-5 w-5 text-gray-400" />
              </div>
              <h3 className="text-base font-bold uppercase tracking-wider text-gray-900 mb-2">
                Læssehøjde og redskaber
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Oplys, hvor materialet skal hen, og hvilke redskaber du har brug for.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-100 text-[11px] text-gray-500">
                Tip: Skal der læsses op i en trailer eller container?
              </div>
            </div>
          </div>

          {/* Redskabs-sektion */}
          <div className="mt-12 rounded-2xl border border-brand-grey bg-white p-6 md:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <span className="text-xs font-bold uppercase tracking-wider text-brand-yellow">
                  Valg af udstyr
                </span>
                <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-gray-900 mt-1 mb-3">
                  Det rette redskab gør forskellen
                </h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  En skovl kan være relevant til løse materialer, mens pallegafler kan være relevante til paller. Det afhænger dog af den konkrete maskine og de redskaber, der er til rådighed. Fortæl os, hvad du skal håndtere, så vi kan undersøge mulighederne og rådgive dig om dit valg.
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-brand-grey bg-brand-light p-4">
                    <span className="block text-xs font-bold uppercase text-gray-900 mb-1">
                      Skovl
                    </span>
                    <span className="text-xs text-gray-600">
                      Ideel til løse masser som muldjord, grus, sand, flis og sne.
                    </span>
                  </div>
                  <div className="rounded-lg border border-brand-grey bg-brand-light p-4">
                    <span className="block text-xs font-bold uppercase text-gray-900 mb-1">
                      Pallegafler
                    </span>
                    <span className="text-xs text-gray-600">
                      Perfekt til flytning af paller, belægningssten og afstillede byggematerialer.
                    </span>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="overflow-hidden rounded-xl border border-brand-grey aspect-16/10">
                  <img
                    src={getOptimizedUrl(actionImage, 800)}
                    alt="Minilæsser i arbejde med skovl og redskab"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 3: Leje af minilæsser, når opgaven er midlertidig */}
      <section className="py-20 md:py-28 border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6">
              <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
                Fleksibel Kapacitet
              </span>
              <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900 leading-tight">
                Leje af minilæsser, <br />
                <span className="text-brand-yellow">når opgaven er midlertidig</span>
              </h2>

              <div className="mt-6 space-y-4 text-base text-gray-700 leading-relaxed">
                <p>
                  Leje af minilæsser er en mulighed, når du har en afgrænset opgave eller mangler ekstra kapacitet i en periode. Du behøver ikke investere i en maskine, du kun bruger indimellem. Hos os kan du få rådgivning om valg af materiel og en fleksibel lejeaftale, der tager udgangspunkt i dit behov.
                </p>
                <p>
                  Skal du leje minilæsser til en travl periode, er det en god idé at tænke over, hvornår maskinen skal i brug, og hvordan den indgår i resten af arbejdet. Vores egne mekanikere vedligeholder løbende materiellet, og vi hjælper dig med at afklare de praktiske spørgsmål, før du vælger.
                </p>
              </div>

              <div className="mt-8 flex items-center gap-4">
                <a
                  href="#kontakt-tilbud"
                  className="inline-flex items-center gap-2 rounded-md bg-brand-yellow px-5 py-3 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-yellow-hover transition-colors"
                >
                  <span>Kontakt for rådgivning</span>
                  <ArrowRight className="h-4 w-4" />
                </a>

                <a
                  href={COMPANY_INFO.lejebetingelserUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md bg-white border border-brand-grey px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700 hover:text-brand-yellow transition-colors"
                >
                  <FileText className="h-4 w-4" />
                  <span>Lejebetingelser</span>
                </a>
              </div>
            </div>

            {/* 3 Nøgleværdier */}
            <div className="lg:col-span-6 space-y-4">
              <div className="rounded-2xl border border-brand-grey bg-brand-light p-6 transition-all duration-200 hover:border-brand-yellow/30 hover:shadow-md">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow text-white shrink-0">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold uppercase tracking-wider text-gray-900 mb-1">
                      Egne mekanikere vedligeholder materiellet
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Maskinerne gennemgår grundigt serviceeftersyn inden udlevering, så du undgår ubehagelige driftstop midt i dit arbejde.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-brand-grey bg-brand-light p-6 transition-all duration-200 hover:border-brand-yellow/30 hover:shadow-md">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow text-white shrink-0">
                    <Clock className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold uppercase tracking-wider text-gray-900 mb-1">
                      Fleksibel lejeperiode efter din tidsplan
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Uanset om du skal bruge minilæsseren i 2 dage, en uge eller gennem et flermåneders entreprenørprojekt, tilpasser vi aftalen.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-brand-grey bg-brand-light p-6 transition-all duration-200 hover:border-brand-yellow/30 hover:shadow-md">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow text-white shrink-0">
                    <Wrench className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold uppercase tracking-wider text-gray-900 mb-1">
                      Ingen kapitallåsning
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Slip for store investeringer, renter og afskrivninger på maskiner, du kun anvender periodisk.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 4: Hvad koster det at leje en minilæsser? */}
      <section className="bg-brand-dark py-20 md:py-28 border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="max-w-3xl mb-12">
            <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
              Pris & Gennemskuelighed
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900">
              Hvad koster det at leje en minilæsser?
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            {/* Tekstblok med SEO-afsnit */}
            <div className="lg:col-span-7 flex flex-col justify-between rounded-2xl border border-brand-grey bg-white p-6 sm:p-8">
              <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                <p>
                  Overvejer du minilæsser leje, bør du se på mere end prisen alene. Den konkrete maskine, lejeperioden og behovet for redskaber spiller ind, når vi finder en løsning til opgaven. Beskriv gerne arbejdet for os, så kan vi give dig et uforpligtende tilbud uden at gætte på en standardpris.
                </p>
                <p>
                  Har du flere opgaver på samme plads, kan det være relevant at se på lejeperioden samlet i stedet for at vurdere hver dag for sig. Fortæl os, om maskinen skal bruges løbende eller til en enkelt etape. Så taler vi om en aftale, der passer til din tidsplan og til det materiel, du har brug for.
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-brand-grey flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-gray-500">
                    Få en præcis pris til din opgave
                  </span>
                  <span className="text-sm font-semibold text-gray-800">
                    Vi sammensætter tilbuddet ud fra maskinbehov og lejetid.
                  </span>
                </div>
                <a
                  href="#kontakt-tilbud"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-yellow px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-yellow-hover transition-colors shrink-0"
                >
                  <span>Indhent tilbud</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>

            {/* 3 Prisfaktorer */}
            <div className="lg:col-span-5 flex flex-col justify-between gap-4">
              <div className="rounded-xl border border-brand-grey bg-white p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-yellow/10 text-brand-yellow font-bold text-xs shrink-0">
                    A
                  </div>
                  <div>
                    <h4 className="text-sm font-bold uppercase text-gray-900">
                      Maskinstørrelse & Løfteevne
                    </h4>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Kompakte modeller til trange rum vs. kraftigere maskiner til tunge løft.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-brand-grey bg-white p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-yellow/10 text-brand-yellow font-bold text-xs shrink-0">
                    B
                  </div>
                  <div>
                    <h4 className="text-sm font-bold uppercase text-gray-900">
                      Lejeperiode (Samlet aftale)
                    </h4>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Fordelagtige vilkår ved samlet ugeleje, månedsleje eller projektforløb.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-brand-grey bg-white p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-yellow/10 text-brand-yellow font-bold text-xs shrink-0">
                    C
                  </div>
                  <div>
                    <h4 className="text-sm font-bold uppercase text-gray-900">
                      Redskaber & Tilbehør
                    </h4>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Skovl, pallegafler, overfaldsskovl m.m. afstemt efter opgavens karakter.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-brand-yellow/30 bg-brand-yellow/5 p-4 text-center">
                <span className="text-xs font-bold text-brand-yellow">
                  Ingen skjulte gebyrer – rene linjer og ærlig rådgivning.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 5: Minilæsser til opgaver i Aalborg, Nordjylland og Aarhus */}
      <section className="py-20 md:py-28 border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="rounded-3xl border border-brand-grey bg-brand-light p-8 md:p-12 lg:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
                  Geografisk Dækning
                </span>
                <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900 leading-tight">
                  Minilæsser til opgaver i <br />
                  <span className="text-brand-yellow">Aalborg, Nordjylland og Aarhus</span>
                </h2>

                <div className="mt-6 space-y-4 text-base text-gray-700 leading-relaxed">
                  <p>
                    Vi har afdelinger i Svenstrup ved Aalborg og i Løgstør og hjælper kunder med opgaver i Nordjylland og Aarhus. Hvis du søger en minilæsser tæt på Aalborg, kan du også læse mere om vores <Link to="/maskinudlejning" className="text-brand-yellow font-semibold hover:underline">materieludlejning i Aalborg</Link>. Fortæl os, hvor du skal arbejde, så vi kan tale om mulighederne.
                  </p>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-grey px-3 py-1 text-xs font-bold text-gray-800">
                    <MapPin className="h-3.5 w-3.5 text-brand-yellow" />
                    <span>Svenstrup J (Aalborg)</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-grey px-3 py-1 text-xs font-bold text-gray-800">
                    <MapPin className="h-3.5 w-3.5 text-brand-yellow" />
                    <span>Løgstør (Vesthimmerland)</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-grey px-3 py-1 text-xs font-bold text-gray-800">
                    <MapPin className="h-3.5 w-3.5 text-brand-yellow" />
                    <span>Aarhus & Østjylland</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-grey px-3 py-1 text-xs font-bold text-gray-800">
                    <MapPin className="h-3.5 w-3.5 text-brand-yellow" />
                    <span>Hurtig levering med egne chauffører</span>
                  </span>
                </div>
              </div>

              <div className="lg:col-span-5 space-y-4">
                <div className="rounded-2xl border border-brand-grey bg-white p-6 shadow-sm">
                  <div className="flex items-center gap-3 text-gray-900 font-bold uppercase text-sm mb-2">
                    <MapPin className="h-5 w-5 text-brand-yellow shrink-0" />
                    <span>Afdeling Svenstrup</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">
                    Jellingvej 19, 9230 Svenstrup J (tæt ved motorvej E45 med nem afhentning og hurtig udkørsel).
                  </p>
                  <a
                    href={`tel:${COMPANY_INFO.phoneRaw}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-yellow hover:underline"
                  >
                    <span>Ring til udlejningen: {COMPANY_INFO.phone}</span>
                  </a>
                </div>

                <div className="rounded-2xl border border-brand-grey bg-white p-6 shadow-sm">
                  <div className="flex items-center gap-3 text-gray-900 font-bold uppercase text-sm mb-2">
                    <MapPin className="h-5 w-5 text-brand-yellow shrink-0" />
                    <span>Afdeling Løgstør</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">
                    Vores historiske base i Vesthimmerland, hvorfra vi betjener hele oplandet.
                  </p>
                  <span className="text-xs font-semibold text-gray-500">
                    Samme høje service og rådgivning.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Relateret materiel til byggepladsen (Krydshenvisninger til /liftudlejning og /minikran) */}
      <section className="py-16 md:py-20 bg-gray-900 text-white">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-yellow/20 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-brand-yellow">
              Kombiner Dit Materiel
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-white">
              Relateret materiel til din byggeplads
            </h2>
            <p className="mt-3 text-sm sm:text-base text-gray-300">
              Saml hele dit maskinbehov ét sted – vi rådgiver og leverer hurtigt direkte til arbejdsstedet.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Liftudlejning kort */}
            <div className="rounded-3xl border border-gray-800 bg-gray-800/80 p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <span className="text-xs font-black uppercase tracking-wider text-brand-yellow">
                  Arbejde i Højden
                </span>
                <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-white mt-1 mb-3">
                  Skal du også arbejde i højden?
                </h3>
                <p className="text-sm text-gray-300 leading-relaxed mb-6">
                  Når underlaget er klargjort eller materialerne er læsset af, har du måske facade-, montage- eller malerarbejde over terræn? Se vores brede udvalg af sakselifte og bomlifte til ethvert projekt.
                </p>
              </div>

              <div>
                <div className="overflow-hidden rounded-2xl border border-gray-700 bg-gray-900 aspect-16/9 mb-6 shadow-xl">
                  <img
                    src={getOptimizedUrl(liftTeaserImage, 600)}
                    alt="Bomlift til arbejde i højden på byggepladsen"
                    className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to="/liftudlejning"
                    className="inline-flex items-center gap-2 rounded-md bg-brand-yellow px-5 py-3 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-yellow-hover transition-colors"
                  >
                    <span>Se vores liftudlejning</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <a
                    href="#kontakt-tilbud"
                    className="inline-flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors"
                  >
                    <span>Få samlet tilbud</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Minikran kort */}
            <div className="rounded-3xl border border-gray-800 bg-gray-800/80 p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <span className="text-xs font-black uppercase tracking-wider text-brand-yellow">
                  Præcisionsløft i Trange Rum
                </span>
                <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-white mt-1 mb-3">
                  Kompakte minikraner til tunge løft
                </h3>
                <p className="text-sm text-gray-300 leading-relaxed mb-6">
                  Skal der monteres vinduespartier, glas eller ståldragere, hvor pladsen er trang? En minikran løfter præcist og sikkert, hvor almindelige mobilkraner ikke kan komme ind.
                </p>
              </div>

              <div>
                <div className="overflow-hidden rounded-2xl border border-gray-700 bg-gray-900 aspect-16/9 mb-6 shadow-xl">
                  <img
                    src={getOptimizedUrl(craneTeaserImage, 600)}
                    alt="Kompakt minikran til præcisionsløft og tunge emner"
                    className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to="/minikran"
                    className="inline-flex items-center gap-2 rounded-md bg-brand-yellow px-5 py-3 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-yellow-hover transition-colors"
                  >
                    <span>Se vores minikraner</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <Link
                    to="/maskinudlejning"
                    className="inline-flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors"
                  >
                    <span>Se maskinkatalog</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 6: Afsluttende rådgivning & Uforpligtende tilbudsformular */}
      <section id="kontakt-tilbud" className="py-20 md:py-28 bg-white scroll-mt-10">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Venstre side: Rådgivning & Kontaktinfo */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
                  Personlig Rådgivning
                </span>
                <h2 className="mt-3 text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900 leading-tight">
                  Er du i tvivl om størrelse, redskaber eller lejeperiode?
                </h2>
              </div>

              <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                <p>
                  Er du i tvivl om størrelse, redskaber eller lejeperiode, tager vi gerne en snak, før du beslutter dig. Fortæl os om materialerne, adgangsforholdene og hvornår du skal bruge maskinen. <Link to="/kontakt" className="text-brand-yellow font-semibold hover:underline">Kontakt os</Link> for rådgivning og et uforpligtende tilbud på en minilæsser, der passer til opgaven.
                </p>
                <p className="text-sm text-gray-600">
                  Er du erhvervsdrivende? Opret en konto nemt via vores <Link to="/bliv-kunde" className="text-brand-yellow font-semibold hover:underline">erhvervskundeoprettelse</Link>.
                </p>
              </div>

              {/* Direkte udlejere med billede og telefon */}
              <div className="pt-4 border-t border-brand-grey space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  Tal direkte med vores udlejere
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {STAFF.map((member, idx) => (
                    <div
                      key={idx}
                      className="rounded-xl border border-brand-grey bg-brand-light p-4 flex items-center gap-3"
                    >
                      <img
                        src={getOptimizedUrl(member.image, 160)}
                        alt={`${member.name} - ${member.role} hos Vesthimmerlands Materieludlejning`}
                        className="h-12 w-12 rounded-full object-cover border border-brand-grey"
                      />
                      <div className="min-w-0">
                        <span className="block text-xs font-bold uppercase text-gray-900 truncate">
                          {member.name}
                        </span>
                        <span className="block text-[11px] text-gray-500 truncate">
                          {member.role}
                        </span>
                        <a
                          href={`tel:${member.phoneRaw}`}
                          className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-brand-yellow hover:underline"
                        >
                          <Phone className="h-3 w-3" />
                          <span>{member.phone}</span>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hovedkontor Info */}
              <div className="rounded-xl border border-brand-grey bg-brand-dark p-5 space-y-3 text-sm">
                <div className="flex items-center gap-3 text-gray-700">
                  <Phone className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span className="font-bold">{COMPANY_INFO.phone}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Mail className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span className="font-bold">{COMPANY_INFO.email}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <MapPin className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span>{COMPANY_INFO.address}</span>
                </div>
              </div>
            </div>

            {/* Højre side: Formular */}
            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-brand-grey bg-brand-light p-6 sm:p-8 md:p-10 shadow-sm">
                <div className="mb-6">
                  <h3 className="text-xl font-black uppercase tracking-tight text-gray-900">
                    Få et uforpligtende tilbud på minilæsser
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Udfyld formularen med dine ønsker til opgave, materialer og periode, så vender vi hurtigt tilbage.
                  </p>
                </div>

                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
